// src/components/VerifiedCoachRoute.jsx
//
// Route guard for coach/team-scoped pages.
//
// Two ways a user can pass this guard:
//   1. Platform bypass — JWT claim `founder: true` or `support: true`.
//      Scrimly staff always pass, even without a matching team role. This
//      lets support reproduce bugs inside a team they're not on. The
//      claim is set server-side and can't be self-escalated.
//   2. Role/membership match — either the user has an allowed role on
//      their profile (coach / manager / owner) OR, if a `:teamId` route
//      param is present, they're on the team's members subdoc / owner /
//      staffIds array.
//
// We intentionally do NOT accept `userProfile.role === 'admin'` from
// Firestore anymore — that field is client-writable and a self-escalation
// vector. Platform admins come in through the JWT bypass above.
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import usePlatformClaims from '@/hooks/usePlatformClaims';
import { db } from '@/firebase/firebase';
import { doc, getDoc } from 'firebase/firestore';
import Loader from '@/components/Loader';

function normalizeRole(r) {
  if (!r) return null;
  const v = String(r).toLowerCase().trim();
  if (v === 'team_manager') return 'manager';
  if (v === 'pending_team_manager') return 'pending_manager';
  return v;
}

export default function VerifiedCoachRoute({
  children,
  // Include both the legacy ('team_manager', 'owner') and current-verb
  // ('coach', 'manager') spellings so existing users with pre-refactor
  // role strings still pass. `admin` intentionally removed — platform
  // admins pass via the JWT-claims bypass below, not via a Firestore
  // `users/{uid}.role` field.
  allowedRoles = ['coach', 'manager', 'team_manager', 'owner'],
  requireVerified = true,
}) {
  const { firebaseUser, userProfile, loadingAuth } = useAuth();
  const { isFounder, isSupport } = usePlatformClaims();
  const location = useLocation();
  const { teamId } = useParams();

  const [checkingTeam, setCheckingTeam] = useState(Boolean(teamId));
  const [teamAllowed, setTeamAllowed] = useState(false);

  // Normalize allowed roles (so 'team_manager' ~ 'manager')
  const allowedRolesSet = useMemo(() => {
    const s = new Set();
    for (const r of allowedRoles) {
      const n = normalizeRole(r);
      if (n) s.add(n);
    }
    return s;
  }, [allowedRoles]);

  // Normalize user roles (collapse aliases, strip pending variants)
  const normalizedRoles = useMemo(() => {
    const list = new Set();
    if (Array.isArray(userProfile?.roles)) {
      userProfile.roles.forEach((r) => {
        const n = normalizeRole(r);
        if (n) list.add(n);
      });
    }
    if (userProfile?.role) {
      const n = normalizeRole(userProfile.role);
      if (n) list.add(n);
    }
    return [...list];
  }, [userProfile]);

  useEffect(() => {
    let alive = true;

    async function run() {
      if (!teamId || !firebaseUser?.uid) {
        setCheckingTeam(false);
        return;
      }

      try {
        const uid = firebaseUser.uid;

        // Prefer membership subdoc: teams/{teamId}/members/{uid}
        let allowed = false;
        try {
          const memSnap = await getDoc(doc(db, 'teams', teamId, 'members', uid));
          if (memSnap.exists()) {
            const role = normalizeRole(memSnap.data()?.role);
            if (
              role &&
              (
                allowedRolesSet.has(role) ||
                ['owner', 'coach', 'manager'].includes(role) // always privileged for team operations
              )
            ) {
              allowed = true;
            }
          }
        } catch {
          // ignore, fall back to team doc below
        }

        // Fallback: team doc legacy fields
        if (!allowed) {
          const snap = await getDoc(doc(db, 'teams', teamId));
          const t = snap.exists() ? snap.data() : null;
          if (t) {
            const managers = new Set([
              ...(Array.isArray(t?.managerIds) ? t.managerIds : []),
              ...(Array.isArray(t?.managers) ? t.managers : []),
              ...(Array.isArray(t?.staffIds) ? t.staffIds : []),
            ]);
            const owners = new Set(
              [t?.ownerId, t?.owner, t?.createdBy, t?.coachId].filter(Boolean)
            );
            allowed = managers.has(uid) || owners.has(uid);
          }
        }

        if (alive) setTeamAllowed(allowed);
      } catch {
        if (alive) setTeamAllowed(false);
      } finally {
        if (alive) setCheckingTeam(false);
      }
    }

    run();
    return () => {
      alive = false;
    };
  }, [teamId, firebaseUser?.uid, allowedRolesSet]);

  // Wait for auth/profile/team checks
  if (loadingAuth || userProfile === undefined || checkingTeam) return <Loader />;

  // Must be logged in
  if (!firebaseUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If verification is NOT required, any authed user can proceed
  if (!requireVerified) {
    return children ? children : <Outlet />;
  }

  // Platform bypass — Scrimly founders/support agents always pass,
  // regardless of team membership or profile role. Intentionally placed
  // AFTER the `requireVerified:false` branch (that branch already passes)
  // and BEFORE the role/membership check (so staff don't get bounced to
  // /unauthorized when helping a team they're not on).
  if (isFounder || isSupport) {
    return children ? children : <Outlet />;
  }

  // Role-based gate (for global areas without teamId) or membership-based (with teamId)
  const roleAllowed = normalizedRoles.some((r) => allowedRolesSet.has(r));
  if (!roleAllowed && !teamAllowed) {
    const msg = `Your roles: ${normalizedRoles.length ? normalizedRoles.join(', ') : 'none'}. Required: ${[...allowedRolesSet].join(', ')}`;
    return <Navigate to="/unauthorized" state={{ from: location, reason: msg }} replace />;
  }

  return children ? children : <Outlet />;
}
